import { useQuery } from '@tanstack/react-query';
import { Alert, Select, Space, Spin, Table, Tabs, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { GetUniversities, GetUniversitySetup } from '../../helper/apiHelper';
import { useCurrentStudyYear, useStudyYears } from '../../hooks/useStudyYear';
import { StudyLevel } from '../../Types/interfaces';
import type { StudyLineType } from '../../Types/interfaces';
import { AcademicCalendarComponent } from '../AcademicCalendar/AcademicCalendarComponent';

type DepartmentRow = {
  key: string;
  department: string;
  deans: Array<string>;
  projectCoordinators: Array<string>;
};

type StudyLineRow = {
  key: string;
  department: string;
  studyLine: string;
  studyLevel: StudyLevel;
  studyLineType: StudyLineType;
  coordinators: Array<string>;
};

type SessionRow = {
  key: string;
  department: string;
  studyLine: string;
  description: string;
  yearOfStudy: number;
  courseCredit: string;
  hours: number;
};

const getStartYear = (date: string): number => new Date(date).getFullYear();

// a study year set up before a list was saved has no emails for it
const renderEmails = (emails?: Array<string> | null): ReactNode => (
  <Space size={[4, 4]} wrap>
    {(emails ?? []).map((email) => (
      <Tag key={email}>{email}</Tag>
    ))}
  </Space>
);

// Distinct values of a column for its filter
const getFilters = (
  values: Array<string>
): Array<{ text: string; value: string }> =>
  [...new Set(values)].map((value) => ({ text: value, value }));

// The rector can look at every study year created so far: its departments, study lines, sessions
// and calendar. They can't be changed here, the next study year is set up on the study year setup page
export const StudyYearsComponent = (): ReactNode => {
  const { t } = useTranslation();
  const { data: currentStudyYear, isLoading: isLoadingCurrentStudyYear } =
    useCurrentStudyYear();
  const { data: studyYears, isLoading: isLoadingStudyYears } = useStudyYears();
  const [selectedYearStart, setSelectedYearStart] = useState<number>();

  const { data: universities, isLoading: isLoadingUniversities } = useQuery({
    queryKey: [...QUERY_KEYS.UNIVERSITIES],
    queryFn: GetUniversities,
  });
  const university = universities?.[0];

  const yearStart =
    selectedYearStart ??
    (currentStudyYear ? getStartYear(currentStudyYear.startDate) : undefined);

  const { data: setup, isLoading: isLoadingSetup } = useQuery({
    queryKey: [...QUERY_KEYS.UNIVERSITY_SETUP, university?.id, yearStart],
    queryFn: () => GetUniversitySetup(university?.id ?? 0, yearStart),
    enabled: university !== undefined && yearStart !== undefined,
  });

  const yearOptions = useMemo(
    () =>
      [...(studyYears ?? [])]
        .sort((a, b) => getStartYear(b.startDate) - getStartYear(a.startDate))
        .map((studyYear) => ({
          value: getStartYear(studyYear.startDate),
          label: (
            <Space>
              {studyYear.name}
              {studyYear.id === currentStudyYear?.id && (
                <Tag color="blue">{t('academicCalendar.current')}</Tag>
              )}
            </Space>
          ),
        })),
    [currentStudyYear, studyYears, t]
  );

  const departments = setup?.departmentsSetup ?? [];

  const departmentRows: Array<DepartmentRow> = departments.map(
    (department) => ({
      key: department.name,
      department: department.name,
      deans: department.deansEmails,
      projectCoordinators: department.universityProjectCoordinatorEmails,
    })
  );

  const studyLineRows: Array<StudyLineRow> = departments.flatMap((department) =>
    department.studyLines.map((studyLine) => ({
      key: `${department.name}/${studyLine.name}`,
      department: department.name,
      studyLine: studyLine.name,
      studyLevel: studyLine.studyLevel,
      studyLineType: studyLine.studyLineType,
      coordinators: studyLine.universityCoordinatorsEmails,
    }))
  );

  const sessionRows: Array<SessionRow> = departments.flatMap((department) =>
    department.studyLines.flatMap((studyLine) =>
      studyLine.sessions.map((session, index) => ({
        key: `${department.name}/${studyLine.name}/${index}`,
        department: department.name,
        studyLine: studyLine.name,
        description: session.description,
        yearOfStudy: session.yearOfStudy,
        courseCredit: session.courseCredit,
        hours: session.hours,
      }))
    )
  );

  const departmentFilters = getFilters(departments.map((d) => d.name));
  const studyLineFilters = getFilters(studyLineRows.map((s) => s.studyLine));

  const departmentColumns: ColumnsType<DepartmentRow> = [
    { title: t('studyYears.department'), dataIndex: 'department' },
    {
      title: t('studyYears.deans'),
      dataIndex: 'deans',
      render: renderEmails,
    },
    {
      title: t('studyYears.projectCoordinators'),
      dataIndex: 'projectCoordinators',
      render: renderEmails,
    },
  ];

  const studyLineColumns: ColumnsType<StudyLineRow> = [
    {
      title: t('studyYears.department'),
      dataIndex: 'department',
      filters: departmentFilters,
      onFilter: (value, row) => row.department === value,
    },
    { title: t('studyYears.studyLine'), dataIndex: 'studyLine' },
    {
      title: t('studyYears.studyLevel'),
      dataIndex: 'studyLevel',
      render: (studyLevel: StudyLevel) =>
        studyLevel === StudyLevel.Master
          ? t('uniForm.studyLevels.master')
          : t('uniForm.studyLevels.bachelor'),
    },
    { title: t('studyYears.studyLineType'), dataIndex: 'studyLineType' },
    {
      title: t('studyYears.coordinators'),
      dataIndex: 'coordinators',
      render: renderEmails,
    },
  ];

  const sessionColumns: ColumnsType<SessionRow> = [
    {
      title: t('studyYears.department'),
      dataIndex: 'department',
      filters: departmentFilters,
      onFilter: (value, row) => row.department === value,
    },
    {
      title: t('studyYears.studyLine'),
      dataIndex: 'studyLine',
      filters: studyLineFilters,
      onFilter: (value, row) => row.studyLine === value,
    },
    { title: t('studyYears.description'), dataIndex: 'description' },
    {
      title: t('studyYears.yearOfStudy'),
      dataIndex: 'yearOfStudy',
      sorter: (a, b) => a.yearOfStudy - b.yearOfStudy,
    },
    { title: t('studyYears.courseCredit'), dataIndex: 'courseCredit' },
    {
      title: t('studyYears.hours'),
      dataIndex: 'hours',
      sorter: (a, b) => a.hours - b.hours,
    },
  ];

  if (
    isLoadingCurrentStudyYear ||
    isLoadingStudyYears ||
    isLoadingUniversities
  ) {
    return <Spin />;
  }

  if (yearStart === undefined) {
    return (
      <Alert type="info" showIcon message={t('academicCalendar.noStudyYear')} />
    );
  }

  const noDepartments = (
    <Alert type="info" showIcon message={t('studyYears.noDepartments')} />
  );

  return (
    <div style={{ width: '100%' }}>
      <Typography.Title level={3}>{t('homePage.studyYears')}</Typography.Title>

      <Space wrap style={{ marginBottom: 16 }}>
        <Typography.Text strong>
          {t('academicCalendar.studyYear')}:
        </Typography.Text>
        <Select
          value={yearStart}
          options={yearOptions}
          onChange={setSelectedYearStart}
          style={{ minWidth: 180 }}
        />
      </Space>

      <Spin spinning={isLoadingSetup}>
        <Tabs
          items={[
            {
              key: 'departments',
              label: t('uniForm.departmentsLabel'),
              children:
                departmentRows.length > 0 ? (
                  <Table
                    columns={departmentColumns}
                    dataSource={departmentRows}
                    pagination={false}
                  />
                ) : (
                  noDepartments
                ),
            },
            {
              key: 'studyLines',
              label: t('uniForm.studyLinesLabel'),
              children:
                departmentRows.length > 0 ? (
                  <Table
                    columns={studyLineColumns}
                    dataSource={studyLineRows}
                    pagination={false}
                  />
                ) : (
                  noDepartments
                ),
            },
            {
              key: 'sessions',
              label: t('uniForm.sessionsLabel'),
              children:
                departmentRows.length > 0 ? (
                  <Table
                    columns={sessionColumns}
                    dataSource={sessionRows}
                    pagination={false}
                  />
                ) : (
                  noDepartments
                ),
            },
            {
              key: 'calendar',
              label: t('homePage.academicCalendar'),
              children: (
                <AcademicCalendarComponent studyYearStart={yearStart} />
              ),
            },
          ]}
        />
      </Spin>
    </div>
  );
};
